/** Placeholder for the gap between two page groups. */
export const PAGE_ELLIPSIS = 'ellipsis' as const;

export type PageItem = number | typeof PAGE_ELLIPSIS;

/**
 * Page numbers for a pagination bar: always the first and last page, the
 * current page with one neighbour on each side, and an ellipsis wherever the
 * sequence jumps. Keeps a constant width so the bar does not jitter while
 * paging through.
 */
export function paginationRange(current: number, total: number, siblings = 1): PageItem[] {
  if (total <= 1) return [1];

  // First, last, current ± siblings — plus enough padding at the edges to keep
  // the item count stable when the current page sits at the start or the end.
  const window = new Set<number>([1, total]);
  const start = Math.min(Math.max(current - siblings, 2), Math.max(total - 2 * siblings - 1, 2));
  const end = Math.max(
    Math.min(current + siblings, total - 1),
    Math.min(2 * siblings + 2, total - 1),
  );
  for (let page = start; page <= end; page++) window.add(page);

  const pages = [...window].sort((a, b) => a - b);

  const items: PageItem[] = [];
  let previous = 0;
  for (const page of pages) {
    if (previous && page - previous > 1) items.push(PAGE_ELLIPSIS);
    items.push(page);
    previous = page;
  }
  return items;
}
